'use client'

import Link from 'next/link'
import { useAnalytics } from '@/hooks/useAnalytics'
import { SectionHeader } from '@/components/SectionHeader'
import { ProjectLogo } from '@/components/ProjectLogo'
import { TechChip } from '@/components/TechChip'
import { cn } from '@/lib/utils'

// The single-column layout has no neighbouring column to fill, so it keeps the
// list short.
const MOBILE_PROJECT_COUNT = 3

function ArrowIcon(props: React.ComponentPropsWithoutRef<'svg'>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path
        d="M16.17 11l-5.36-5.36 1.41-1.41L20 12l-7.78 7.78-1.41-1.41L16.17 13H4v-2z"
        fill="currentColor"
      />
    </svg>
  )
}

function ProjectIcon(props: React.ComponentPropsWithoutRef<'svg'>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M2.75 9.75a3 3 0 0 1 3-3h12.5a3 3 0 0 1 3 3v8.5a3 3 0 0 1-3 3H5.75a3 3 0 0 1-3-3v-8.5Z"
        className="fill-zinc-100 stroke-zinc-400 dark:fill-zinc-100/10 dark:stroke-zinc-500"
      />
      <path
        d="M3 9.75h18M6.75 6.75l.5-2.5h9.5l.5 2.5"
        className="stroke-zinc-400 dark:stroke-zinc-500"
      />
    </svg>
  )
}

interface TechStack {
  name: string
}

interface Project {
  name: string
  description: string
  link: { href: string; label: string }
  logo: any
  logoScale?: number
  techStack?: TechStack[]
}

export function FeaturedProjects({ projects }: { projects: Project[] }) {
  const { track } = useAnalytics()

  const handleProjectClick = (projectName: string) => {
    track('project_view', {
      project_name: projectName,
      source: 'home_featured',
    })
  }

  const handleTechClick = (techName: string, projectName: string) => {
    track('tech_stack_interest', {
      technology: techName,
      project: projectName,
      location: 'home_featured',
    })
  }

  return (
    // On desktop the card stretches to the height of the neighbouring column
    // and the rows spread to fill it, so both columns end on the same line.
    <div className="rounded-2xl border border-zinc-100 p-6 lg:flex lg:flex-1 lg:flex-col dark:border-zinc-700/40">
      <SectionHeader icon={ProjectIcon} label="Featured Projects" />
      <ul className="mt-6 flex flex-col gap-6 lg:flex-1 lg:justify-between lg:gap-4">
        {projects.map((project, index) => (
          <li
            key={project.name}
            className={cn(
              'group relative gap-4',
              index < MOBILE_PROJECT_COUNT ? 'flex' : 'hidden lg:flex',
            )}
          >
            <ProjectLogo
              src={project.logo}
              alt={project.name}
              size={10}
              logoScale={project.logoScale}
            />
            <div className="flex flex-auto flex-col">
              <h3 className="project-title text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                <Link
                  href={project.link.href}
                  onClick={() => handleProjectClick(project.name)}
                >
                  <span className="absolute inset-0 rounded-md group-hover:bg-zinc-50/50 dark:group-hover:bg-zinc-800/50" />
                  {project.name}
                </Link>
              </h3>
              <p className="mt-1 line-clamp-2 text-sm text-zinc-600 dark:text-zinc-400">
                {project.description}
              </p>

              {/* Tech Stack */}
              {project.techStack && (
                <div className="mt-2 flex flex-wrap gap-1.5 transition-opacity group-hover:opacity-70">
                  {project.techStack.map((tech) => (
                    <TechChip
                      key={tech.name}
                      className="relative z-10 flex cursor-pointer items-center rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-800 dark:bg-zinc-800/80 dark:text-zinc-200"
                      label={tech.name}
                      logoName={tech.name}
                      logoSize={12}
                      logoClassName="mr-1 size-3"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleTechClick(tech.name, project.name)
                      }}
                    />
                  ))}
                </div>
              )}

              <Link
                href={project.link.href}
                onClick={() => handleProjectClick(project.name)}
                className="relative z-10 mt-3 flex items-center text-sm font-medium text-teal-700 transition-colors hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-300"
              >
                <span>{project.link.label}</span>
                <ArrowIcon className="ml-1 size-4 stroke-current" />
              </Link>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-6">
        <Link
          href="/projects"
          className="flex items-center text-sm font-medium text-teal-700 dark:text-teal-400"
          onClick={() =>
            track('project_view', {
              project_name: 'all_projects',
              source: 'home_featured',
            })
          }
        >
          <span>View all projects</span>
          <ArrowIcon className="ml-1 size-4 stroke-current" />
        </Link>
      </div>
    </div>
  )
}
